import React, { useMemo } from 'react';
import { Button, Skeleton, Text } from '@patternfly/react-core';
import SVG from 'react-inlinesvg';

import { FeaturedCard } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import LessonIndicators from 'components/Catalog/Card/Lesson/LessonIndicators';
import classNames from 'classnames';

import {
  CatalogEntry,
  CourseSubformat,
  CourseType,
  DeliveryFormat,
  Modality,
  OfferingStatus,
} from 'types';
import { ProgressByCourse } from 'types/progress';

import './styles.scss';

import CourseIcon from 'img/icons/offering_icons/course_icon.svg';
import TechOverviewIcon from 'img/icons/offering_icons/techoverview_icon.svg';
import LessonLabPlusIcon from 'img/icons/offering_icons/lesson_labplus_icon.svg';

// Uncomment for other delivery formats when needed:
import ExpertExtrasIcon from 'img/icons/offering_icons/expertextras_icon.svg';
// import SkillsPathsIcon from 'img/icons/offering_icons/skillspaths_icon.svg';
// import VideoIcon from 'img/icons/offering_icons/video_icon.svg';
import EarlyAccessIcon from 'img/icons/offering_icons/earlyaccess_icon.svg';
// import ExamExperienceIcon from 'img/icons/offering_icons/examexperience_icon.svg';
// import LabIcon from 'img/icons/offering_icons/lab_icon.svg';
// import PremiumIcon from 'img/icons/offering_icons/premium_icon.svg';
// import ExamIcon from 'img/icons/offering_icons/exam_icon.svg';

import useOfferingActivityStats from './useOfferingActivityStats';
import { IDeliveryConfig } from './types';

type Props = Pick<
  Optional<ProgressByCourse, 'total_progress'>,
  'total_progress'
> & {
  entry?: CatalogEntry;
  deliveryFormat: DeliveryFormat;
  className?: string;
  isLoading?: boolean;
} & DataAnalyticsId;

const FeaturedOfferingCard = ({
  entry,
  total_progress,
  deliveryFormat,
  className,
  isLoading,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const { routerStore } = useMobxStores();
  const slug = entry?.slug;
  const title = entry?.title;
  const isContinue = (total_progress ?? 0) > 0;

  const getConfigForDeliveryFormat = (
    hasProgress: boolean,
  ): IDeliveryConfig => {
    const courseRoute = hasProgress ? 'courses:view' : 'courses:overview';
    // TODO: This can be improved for better reusability and maintainability.
    // for instance, we can create an icon component that takes the delivery format and returns the icon, title, background color, and color.
    // we can also create a function that returns the route name and params based on the entry.
    switch (deliveryFormat) {
      case Modality.Course:
        return {
          icon: CourseIcon,
          title: t('Course'),
          backgroundColor: 'var(--rhls-offering-course)',
          color: 'var(--rhls-offering-course-icon-color)',
          // TODO: In the future change this to a link
          onContinue: () => {
            routerStore.navigate(courseRoute, { course: slug });
          },
          hasStats: true,
        };
      case CourseType.Lesson:
        return {
          icon: LessonLabPlusIcon,
          title: t('Lesson'),
          backgroundColor: 'var(--rhls-offering-lesson-lab-plus)',
          color: 'var(--rhls-offering-lesson-lab-plus-icon-color)',
          onContinue: () => {
            routerStore.navigate(courseRoute, { course: slug });
          },
          hasStats: true,
        };
      case CourseSubformat.CompReview:
        return {
          icon: LessonLabPlusIcon,
          title: t('Comprehensive review'),
          backgroundColor: 'var(--rhls-offering-lesson-lab-plus)',
          color: 'var(--rhls-offering-lesson-lab-plus-icon-color)',
          onContinue: () => {
            routerStore.navigate(courseRoute, { course: slug });
          },
          hasStats: true,
        };
      case CourseType.LabPlus:
        return {
          icon: LessonLabPlusIcon,
          title: t('Lab Plus'),
          backgroundColor: 'var(--rhls-offering-lesson-lab-plus)',
          color: 'var(--rhls-offering-lesson-lab-plus-icon-color)',
          onContinue: () => {
            routerStore.navigate(courseRoute, { course: slug });
          },
          hasStats: true,
        };
      case Modality.TechOverview:
        return {
          icon: TechOverviewIcon,
          title: t('Technical overview'),
          backgroundColor: 'var(--rhls-offering-tech-overview)',
          color: 'var(--rhls-offering-tech-overview-icon-color)',
          onContinue: () => {
            routerStore.navigate('tech-overview:view', { slug });
          },
          hasStats: false,
        };
      case Modality.ExpertSeminar:
        return {
          icon: ExpertExtrasIcon,
          title: t('Expert extra'),
          backgroundColor: 'var(--rhls-offering-expert-extra)',
          color: 'var(--rhls-offering-expert-extra-icon-color)',
          onContinue: () => {
            routerStore.navigate('expertSeminars:view', { slug });
          },
          hasStats: false,
        };
      case OfferingStatus.EarlyAccess:
        return {
          icon: EarlyAccessIcon,
          title: t('Early access'),
          backgroundColor: 'var(--rhls-offering-early-access)',
          color: 'var(--rhls-offering-early-access-icon-color)',
          onContinue: () => {
            routerStore.navigate(courseRoute, { course: slug });
          },
          hasStats: false,
        };
      default:
        return null;
    }
  };

  const config = useMemo(
    () => getConfigForDeliveryFormat(isContinue),
    [deliveryFormat, slug, isContinue],
  );

  const { stats: lastAccessedOfferingStats, isLoading: isLoadingStats } =
    useOfferingActivityStats({ slug, config });

  if (isLoading) {
    return (
      <FeaturedCard className={classNames('featured-offering-card', className)}>
        <FeaturedCard.Body>
          <div className="pf-v5-theme-dark">
            <Skeleton width="100%" height="60px" />
            <Skeleton
              width="100%"
              height="10px"
              style={{ marginTop: '54px' }}
            />
          </div>
        </FeaturedCard.Body>
        <FeaturedCard.Footer>
          <div className="pf-v5-theme-dark">
            <Skeleton width="140px" height="50px" />
          </div>
        </FeaturedCard.Footer>
      </FeaturedCard>
    );
  }

  if (!entry || !config) {
    return (
      <FeaturedCard
        className={classNames('featured-offering-card ', className)}
      >
        <div className="pf-v5-theme-dark featured-offering-card__empty-container">
          <Text>{t('No recent activity.')}</Text>
        </div>
        <FeaturedCard.Footer>
          <div className="pf-v5-theme-dark">
            <Button
              className="upcoming-events__empty-btn"
              variant="secondary"
              onClick={() => {
                routerStore.navigate('catalog');
              }}
            >
              {t('Explore catalog')}
            </Button>
          </div>
        </FeaturedCard.Footer>
      </FeaturedCard>
    );
  }

  return (
    <FeaturedCard className={classNames('featured-offering-card', className)}>
      <FeaturedCard.Header
        title={title || slug}
        icon={<SVG src={config.icon} title={config.title} />}
        backgroundColor={config.backgroundColor}
        color={config.color}
      />
      {config.hasStats ? (
        <FeaturedCard.Body>
          {isLoadingStats ? (
            <div className="pf-v5-theme-dark featured-offering-card__indicators-loading">
              <Skeleton
                height="100%"
                screenreaderText="Loading small rectangle contents"
              />
              <Skeleton
                height="100%"
                screenreaderText="Loading small rectangle contents"
              />
            </div>
          ) : (
            <LessonIndicators
              className="pf-v5-theme-dark featured-offering-card__indicators"
              includeLabels
              disableLegend
              asGrid
              // stats indicators:
              timeInvested={lastAccessedOfferingStats?.time_invested}
              labTimeInvested={lastAccessedOfferingStats?.lab_time_invested}
            />
          )}
        </FeaturedCard.Body>
      ) : (
        <FeaturedCard.Body>{null}</FeaturedCard.Body>
      )}
      <FeaturedCard.Progress progress={(total_progress ?? 0.0001) * 100} />
      <FeaturedCard.Footer>
        <Button
          className="featured-offering-card__continue-btn"
          data-analytics-id={props['data-analytics-id']}
          variant="danger"
          // TODO: ideally this should be a Link component
          // so when we migrate the router, we can just use the Link component
          onClick={config.onContinue}
        >
          {isContinue ? t('Continue') : t('View')}
        </Button>
      </FeaturedCard.Footer>
    </FeaturedCard>
  );
};

export default withErrorBoundary(
  observer(FeaturedOfferingCard),
  ComponentError,
  handleError,
);
